// Source: ERP isValidIBANNumber CODE_LENGTHS (issue #1488)
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24,
  AE: 23,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  CH: 21,
  CR: 21,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  TN: 24,
  TR: 26,
} as const satisfies Record<string, number>

const IBAN_MIN = 15
const IBAN_MAX = 34

const normalizeAlphanumeric = (raw: string) =>
  raw.replace(/[\s-]+/g, '').toUpperCase()

/**
 * Validates an IBAN using the ISO 13616 mod-97 checksum.
 * When allowedCountries is provided, also enforces country membership and
 * exact per-country length (matching ERP's CODE_LENGTHS: AT/DE/ES/IT).
 */
const validateIban = (raw: string, allowedCountries?: string[]): boolean => {
  const iban = normalizeAlphanumeric(raw)
  const country = iban.slice(0, 2)

  if (allowedCountries) {
    if (!allowedCountries.includes(country)) {
      return false
    }
    const expectedLength = IBAN_LENGTHS[country]
    if (expectedLength == null || iban.length !== expectedLength) {
      return false
    }
  } else {
    if (iban.length < IBAN_MIN || iban.length > IBAN_MAX) {
      return false
    }
  }

  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) {
    return false
  }

  const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`
  let mod = 0
  for (const char of rearranged) {
    const digits = /\d/.test(char) ? char : String(char.charCodeAt(0) - 55)
    for (const d of digits) {
      mod = (mod * 10 + Number(d)) % 97
    }
  }
  return mod === 1
}

// Modulo 23 lookup for NIF/NIE checksum
// @see https://es.wikipedia.org/wiki/N%C3%BAmero_de_identificaci%C3%B3n_fiscal#C%C3%B3digo_de_control_para_n%C3%BAmeros_de_DNI_o_NIE
const NIF_NIE_CHECKSUM_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE'

/**
 * Validates a Spanish NIF (DNI) — 8 digits + 1 checksum letter.
 * @see https://es.wikipedia.org/wiki/N%C3%BAmero_de_identificaci%C3%B3n_fiscal
 */
const validateNif = (raw: string): boolean => {
  const normalized = normalizeAlphanumeric(raw)
  if (!/^\d{8}[A-Z]$/.test(normalized)) {
    return false
  }
  const number = Number(normalized.slice(0, 8))
  return normalized[8] === NIF_NIE_CHECKSUM_LETTERS[number % 23]
}

/**
 * Validates a Spanish NIE (foreigner ID) — X/Y/Z prefix + 7 digits + 1 checksum letter.
 * @see https://en.wikipedia.org/wiki/NIE_number
 */
const validateNie = (raw: string): boolean => {
  const normalized = normalizeAlphanumeric(raw)
  if (!/^[XYZ]\d{7}[A-Z]$/.test(normalized)) {
    return false
  }
  const prefixMap = { X: '0', Y: '1', Z: '2' } as const
  const numericValue = Number(
    `${prefixMap[normalized[0] as keyof typeof prefixMap]}${normalized.slice(
      1,
      8
    )}`
  )
  return normalized[8] === NIF_NIE_CHECKSUM_LETTERS[numericValue % 23]
}

// @see https://en.wikipedia.org/wiki/Italian_fiscal_code
const IT_FISCAL_CODE_REGEX =
  /^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i

const validateItFiscalCode = (value: string): boolean =>
  IT_FISCAL_CODE_REGEX.test(value)

const VAT_PATTERNS: Record<string, RegExp> = {
  DE: /^(DE)?\d{9}$/i,
  PL: /^(PL)?\d{10}$/i,
}

const VAT_VALIDATORS: Record<string, (value: string) => boolean> = {
  IT: validateItFiscalCode,
}

/**
 * Validates a VAT / fiscal ID for the given ISO country code.
 * Returns true for unknown countries when the value is longer than 4 characters.
 * Note: IT uses a fiscal code (codice fiscale), not a VAT number — use ValidationUtils.it.fiscalCode directly when that distinction matters.
 */
const validateVat = (value: string, countryCode: string): boolean => {
  const code = countryCode.toUpperCase()
  const validator = VAT_VALIDATORS[code]
  if (validator) {
    return validator(value)
  }
  const pattern = VAT_PATTERNS[code]
  if (!pattern) {
    return value.length > 4
  }
  return pattern.test(value)
}

export const ValidationUtils = {
  normalize: normalizeAlphanumeric,
  iban: {
    validate: validateIban,
  },
  es: {
    nif: validateNif,
    nie: validateNie,
  },
  it: {
    fiscalCode: validateItFiscalCode,
  },
  vat: {
    validate: validateVat,
  },
}
